import { MAX_ROW_LIMIT, buildResource, runQueries } from '../GscTools/query';
import { segmentFilter } from './brand';

/**
 * The requests this report makes, one function per block.
 *
 * Every one of them is a filtered ask: the brand side carries the compiled pattern as
 * `includingRegex`, the non-brand side carries the same pattern as `excludingRegex`, and nothing
 * is ever derived by subtracting one from an unfiltered total. The two sides are complementary
 * over the queries Search Console was willing to return, and the gap between their sum and the
 * property's own traffic is the queries it withheld — a gap this report names rather than
 * assigns.
 *
 * Calls are grouped by side rather than by period: a side's two windows share one call, so the
 * brand figures and the non-brand figures fail independently. That is the shape the comparison
 * needs, because a share computed when one side did not come back would be a share of a number
 * the report does not have.
 *
 * `auto` aggregation throughout. The report asks about queries, and letting Search Console pick
 * its own aggregation is what keeps a filtered total comparable with the unfiltered one it is
 * read beside — choosing `byProperty` here would be claiming a property aggregate for rows that
 * were selected by a query filter.
 */

/** The parts of the scope every request on this report carries, besides its own filter. */
function scopeRequest(scope) {
    return {
        searchType: scope.searchType,
        country: scope.country,
        device: scope.device,
        dataState: scope.dataState,
    };
}

/**
 * Runs one side of the rule over both windows in a single call.
 *
 * The baseline query is simply left out when there is no comparison, rather than sent and
 * ignored, so "no comparison" costs nothing.
 */
async function fetchSide({ credentialsDomainId, scope, segment, dimensions, periods }) {
    const filter = segmentFilter(segment, scope.compiledPattern);

    const queries = Object.entries(periods)
        .filter(([, period]) => !!period)
        .map(([key, period]) => ({
            key,
            resource: buildResource({
                period,
                dimensions,
                aggregationType: 'auto',
                ...scopeRequest(scope),
                filters: [filter],
            }),
        }));

    if (!queries.length) return {};

    return runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries,
    });
}

/**
 * Block 02 — one side's totals for each window, asked for with no dimension at all.
 *
 * A dimensionless request is the only thing that answers "how much traffic matched this rule":
 * summing the daily rows of block 03 instead would give the total of the days Search Console
 * chose to return, which is a different number and a quieter one.
 */
export async function fetchSegmentTotals({ credentialsDomainId, scope, segment }) {
    const results = await fetchSide({
        credentialsDomainId,
        scope,
        segment,
        dimensions: [],
        periods: { current: scope.current, baseline: scope.baseline },
    });

    return { current: results.current ?? [], baseline: results.baseline ?? [] };
}

/**
 * Block 03 — one side's daily rows for each window.
 *
 * Requested per side with the same filter the totals used, so the lines and the cards above them
 * are answers to the same question. A day missing from one side is a day Search Console returned
 * nothing for on that side; it stays a gap rather than becoming a zero, and it is never filled in
 * from the other side.
 */
export async function fetchSegmentDaily({ credentialsDomainId, scope, segment }) {
    const results = await fetchSide({
        credentialsDomainId,
        scope,
        segment,
        dimensions: ['date'],
        periods: { current: scope.current, baseline: scope.baseline },
    });

    return { current: results.current ?? [], baseline: results.baseline ?? [] };
}

/**
 * Block 04 — the queries themselves, on one side of the rule, for one window.
 *
 * This is the only request that names a query dimension, which is why it is sent for a single
 * window and on demand rather than for both windows on every apply. What comes back is the
 * evidence Search Console was willing to show: it is a sample of the side, never the whole of it,
 * and the block says so rather than letting a short list read as a small segment.
 */
export async function fetchEvidence({ credentialsDomainId, scope, segment, period }) {
    const results = await runQueries({
        credentialsDomainId,
        siteUrl: scope.siteUrl,
        maxCount: MAX_ROW_LIMIT,
        queries: [
            {
                key: 'queries',
                resource: buildResource({
                    period,
                    dimensions: ['query'],
                    aggregationType: 'auto',
                    ...scopeRequest(scope),
                    filters: [segmentFilter(segment, scope.compiledPattern)],
                }),
            },
        ],
    });

    return results.queries ?? [];
}
